import { forwardRef, useContext } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import { Button } from '@dynatrace/strato-components/buttons';
import {
  NotificationIcon,
  NotificationActiveIcon,
} from '@dynatrace/strato-icons';

import { NotificationSettingsContext } from '../../../notifications/notification-settings/contexts/NotificationSettingsContext.js';
import type { TriggerProps } from '../../../overlays/hooks/useOverlayWithTrigger.js';
import { notifyButtonTranslations } from '../notify-button-messages.js';
import type { NotifyButtonProps } from '../NotifyButton.js';

/**
 * @internal
 */
export const NotifyButtonTrigger = forwardRef<
  HTMLButtonElement,
  Omit<NotifyButtonProps, 'type' | 'config'> & {
    triggerProps: TriggerProps;
  }
>((props, ref) => {
  const intl = useIntl();

  const { loading, hasNotFoundError, enabled } = useContext(
    NotificationSettingsContext,
  );

  const {
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    className: consumerClassName,
    style: consumerStyle,
    disabled,
    readOnly,
    size,
    variant,
    showLabel,
    triggerProps,
    ...remainingProps
  } = props;

  return (
    <Button
      ref={ref}
      aria-label={intl.formatMessage(notifyButtonTranslations.toggleOverlay)}
      loading={loading}
      size={size}
      variant={variant}
      disabled={hasNotFoundError || disabled}
      className={consumerClassName}
      style={consumerStyle}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      data-testid={dataTestId}
      readOnly={readOnly}
      {...remainingProps}
      {...(readOnly ? {} : triggerProps)}
    >
      <Button.Prefix>
        {enabled ? <NotificationActiveIcon /> : <NotificationIcon />}
      </Button.Prefix>
      {showLabel && (
        <FormattedMessage {...notifyButtonTranslations.notifications} />
      )}
    </Button>
  );
});
